import { useState } from "react";
import "./StackedBarChart.scss";

export interface StackSeries {
  key: string;
  label: string;
  color: string;
}

export interface StackDatum {
  label: string;
  /** 各 series.key 对应的数值 */
  values: Record<string, number>;
}

/** 取一个不小于 v 的“整齐”上界（1/2/5 × 10^n），用于 Y 轴刻度 */
function niceCeil(v: number): number {
  if (v <= 0) return 1;
  const exp = Math.floor(Math.log10(v));
  const base = Math.pow(10, exp);
  const n = v / base;
  const nice = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return nice * base;
}

const TICKS = 4; // Y 轴分 4 段（5 条线）

/**
 * 堆叠柱状图（纯 CSS）：每根柱子把多个 series 的数值从底部往上叠加。
 * 横轴为日期，纵轴为数量；带 Y 轴刻度、网格线、图例，鼠标 hover 显示精确数值。
 */
const StackedBarChart: React.FC<{
  series: StackSeries[];
  data: StackDatum[];
  height?: number;
  /** 最多显示多少个 X 轴标签 */
  maxLabels?: number;
}> = ({ series, data, height = 240, maxLabels = 10 }) => {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  if (!data.length) {
    return <div className="cs-stack__empty">暂无数据</div>;
  }

  const totals = data.map((d) =>
    series.reduce((sum, s) => sum + (d.values[s.key] || 0), 0)
  );
  const maxTotal = niceCeil(Math.max(...totals, 1));
  const labelEvery = Math.max(1, Math.ceil(data.length / maxLabels));

  const yTicks = Array.from({ length: TICKS + 1 }).map((_, i) =>
    Math.round((maxTotal * (TICKS - i)) / TICKS)
  );

  const hovered = hoverIdx != null ? data[hoverIdx] : null;
  // tooltip 水平位置（柱子中心），并做边缘钳制
  const tipLeft =
    hoverIdx != null
      ? Math.min(92, Math.max(8, ((hoverIdx + 0.5) / data.length) * 100))
      : 0;

  return (
    <div className="cs-stack">
      <div className="cs-stack__plot">
        {/* Y 轴 */}
        <div className="cs-stack__yaxis" style={{ height }}>
          {yTicks.map((t, i) => (
            <div key={i} className="cs-stack__ytick">
              {t.toLocaleString("en-US")}
            </div>
          ))}
        </div>

        {/* 柱子区 */}
        <div className="cs-stack__bars-wrap" style={{ height }}>
          {/* 网格线 */}
          <div className="cs-stack__grid">
            {Array.from({ length: TICKS + 1 }).map((_, i) => (
              <div key={i} className="cs-stack__grid-line" />
            ))}
          </div>

          <div className="cs-stack__bars">
            {data.map((d, i) => (
              <div
                key={`${d.label}-${i}`}
                className={`cs-stack__col${
                  hoverIdx === i ? " cs-stack__col--active" : ""
                }`}
                onMouseEnter={() => setHoverIdx(i)}
                onMouseLeave={() => setHoverIdx(null)}
              >
                {series.map((s) => {
                  const v = d.values[s.key] || 0;
                  if (v <= 0) return null;
                  return (
                    <div
                      key={s.key}
                      className="cs-stack__seg"
                      style={{
                        height: `${(v / maxTotal) * 100}%`,
                        background: s.color,
                      }}
                    />
                  );
                })}
              </div>
            ))}
          </div>

          {/* Hover 提示：显示当天各 API 精确数值 + 总量 */}
          {hovered && (
            <div
              className="cs-stack__tip"
              style={{ left: `${tipLeft}%` }}
            >
              <div className="cs-stack__tip-date">{hovered.label}</div>
              {series.map((s) => (
                <div key={s.key} className="cs-stack__tip-row">
                  <span
                    className="cs-stack__tip-dot"
                    style={{ background: s.color }}
                  />
                  <span className="cs-stack__tip-label">{s.label}</span>
                  <span className="cs-stack__tip-val">
                    {(hovered.values[s.key] || 0).toLocaleString("en-US")}
                  </span>
                </div>
              ))}
              <div className="cs-stack__tip-total">
                <span className="cs-stack__tip-label">Total</span>
                <span className="cs-stack__tip-val">
                  {totals[hoverIdx as number].toLocaleString("en-US")}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* X 轴日期 */}
      <div className="cs-stack__labels">
        {data.map((d, i) => (
          <div key={`label-${d.label}-${i}`} className="cs-stack__label">
            {i % labelEvery === 0 ? d.label : ""}
          </div>
        ))}
      </div>

      {/* 图例 */}
      <div className="cs-stack__legend">
        {series.map((s) => (
          <div key={s.key} className="cs-stack__legend-item">
            <span
              className="cs-stack__swatch"
              style={{ background: s.color }}
            />
            {s.label}
          </div>
        ))}
      </div>
    </div>
  );
};

export default StackedBarChart;
